import { Col, Form, FormControl, Row } from 'react-bootstrap';
import Select from 'react-select';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import * as Yup from 'yup';
import RequiredFormLabel from '../../RequiredFormLabel/RequiredFormLabel';
import Btn from '../../../components/Btn/Btn';
import { renderFloatAsStr, strToFloat } from '../../../utils';
import { t } from 'i18next';
import { RoleEnum } from '../../../api';
import { useAuth } from '../../../hooks/useAuth';
import TiassistoElement from '../../TiAssisto/TiassistoElement';
import useLeadStore from '../../../store/LeadsStore';
import useModalStore from '../../../store/ModalStore';
import { useState, useEffect, useMemo } from 'react';
import { useApiClient } from '../../../hooks/useApiClient';
import { RemoteSelectOption } from 'src/types';
import _ from 'lodash';
import ValidationError from 'src/components/ValidationError/ValidationError';
import { FaInfoCircle } from 'react-icons/fa';
import ToolTip from 'src/components/Tooltip/Tooltip';
import { useLocation } from 'react-router-dom';
import { useOpportunityUserFields } from '../../../hooks/useOpportunityUserFields';
import { UserDTO } from '../../../api';
import { applySegnalatoreCrossUpdate } from 'src/utils/opportunity';

export const getOpportunityCreationValidationSchema = (userRole: RoleEnum) => {
  const isMasterOrBackoffice = [RoleEnum.MASTER, RoleEnum.BACKOFFICE].includes(userRole);

  return Yup.object().shape({
    name: Yup.string().required(t('required_field')),
    commissions: isMasterOrBackoffice
      ? Yup.string().test('at-least-one', t('required_field'), function (val) {
        return (
          !!(val && strToFloat(val) >= 0) ||
          !!(this.parent.provvigioniPassive && strToFloat(this.parent.provvigioniPassive) >= 0)
        );
      })
      : Yup.string()
        .required(t('required_field'))
        .test((val) => strToFloat(val) >= 0),
    provvigioniPassive: isMasterOrBackoffice
      ? Yup.string().test('at-least-one', t('required_field'), function (val) {
        return (
          !!(this.parent.commissions && strToFloat(this.parent.commissions) >= 0) ||
          !!(val && strToFloat(val) >= 0)
        );
      })
      : Yup.string().notRequired(),
    segnalatore: Yup.string().when('Canale__c', {
      is: (val: any) => val?.value?.toLowerCase() === 'segnalatore',
      then: (schema) => schema.required(t('required_field')),
      otherwise: (schema) => schema.notRequired(),
    }),
    provvigioniSegnalatore: Yup.string().when('Canale__c', {
      is: (val: any) => val?.value?.toLowerCase() === 'segnalatore',
      then: (schema) =>
        schema.required(t('required_field')).test((val) => strToFloat(val) >= 0),
      otherwise: (schema) => schema.notRequired(),
    }),
    Canale__c: Yup.object().required(t('required_field')),
    Noleggiatore__c: Yup.object().required(t('required_field')),
    Tipologia__c: Yup.object().required(t('required_field')),
    driveItEasy: Yup.boolean(),
    driverEmail: Yup.string().when('driveItEasy', {
      is: true,
      then: (schema) =>
        schema.email(t('invalid_email')).required(t('required_field')),
      otherwise: (schema) => schema.notRequired(),
    }),
    Offerta_Edison__c: Yup.object().notRequired(),
    salesUser: Yup.string().required(t('required_field')),
    referenceUserId: Yup.string().required(t('required_field')),
    backupUsers: Yup.array().of(Yup.string()),
  });
};

export const ForceCommissionsZeroOnSegnalazione = ({
  isSegnalazione,
  isMasterOrBackoffice,
  setFieldValue,
}) => {
  useEffect(() => {
    if (isMasterOrBackoffice && isSegnalazione) {
      setFieldValue('commissions', '0');
    }
  }, [isMasterOrBackoffice, isSegnalazione, setFieldValue]);
  return null;
};


export const ForceLeadSourceOnSegnalazione = ({
  isSegnalazione,
  hmpBridgeLeadSource,
  setFieldValue,
}) => {
  useEffect(() => {
    if (isSegnalazione && hmpBridgeLeadSource) {
      setFieldValue('LeadSource', hmpBridgeLeadSource);
    }
  }, [isSegnalazione, hmpBridgeLeadSource, setFieldValue]);
  return null;
};

export const RecomputeSegnalatoreOnPercChange = ({
  isSegnalazione,
  isMasterOrBackoffice,
  selectedSegnalatorePerc,
  dealerValue,
  setFieldValue,
}: {
  isSegnalazione: boolean;
  isMasterOrBackoffice: boolean;
  selectedSegnalatorePerc: string | number | null | undefined;
  dealerValue: string | number | undefined;
  setFieldValue: (field: string, value: any) => void;
}) => {
  useEffect(() => {
    if (!isSegnalazione) return;
    if (dealerValue === '' || dealerValue === undefined || dealerValue === null) return;
    applySegnalatoreCrossUpdate({
      field: isMasterOrBackoffice ? 'provvigioniPassive' : 'commissions',
      value: String(dealerValue),
      isMasterOrBackoffice,
      segnalatorePerc: selectedSegnalatorePerc,
      setFieldValue,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSegnalatorePerc, isSegnalazione]);
  return null;
};

const OpportunityCreation = ({
  account,
  setOpportunityValues,
  opportunityValues,
  opportunityFields,
  onHide = () => { },
}) => {
  const { t } = useTranslation();
  const { apiClient, handleApiResponse } = useApiClient();
  const { userRole, userReferencePartnerList, user, userIsSalesforceSales } =
    useAuth();
  const { allSalesUsersDTO, allSegnalatori, isBam, isMasterOrBackoffice } = useOpportunityUserFields(userRole);

  const commissionsLabel = isMasterOrBackoffice
    ? t('provvigioni_canale')
    : userRole === RoleEnum.AREA_MANAGER
      ? t('provvigioni_dealer_euro')
      : t('commissions');
  const [salesUsers, setSalesUsers] = useState([]);
  const [referenceUsers, setReferenceUsers] = useState<RemoteSelectOption[]>(
    []
  );
  let location = useLocation();
  const [backupUsers, setBackupUsers] = useState([]);
  const { setLeadWizardData } = useLeadStore();
  const { modalState } = useModalStore();
  const submitFn = (values) => {
    const refUser = referenceUsers?.find(
      (item) => item?.value === values?.referenceUserId
    );
    setOpportunityValues({
      ...opportunityValues,
      ...values,
      referenceUserLabel: refUser?.label,
    });
    setLeadWizardData({
      opportunity: { ...values, referenceUserLabel: refUser?.label },
    });
    onHide();
  };

  const isQuoteOpportunity = location.pathname?.includes('/opportunity-wizard')

  function isButtonDisabled(userRole) {
    return ![
      RoleEnum.SALES,
      RoleEnum.SALES_DEALER,
      RoleEnum.SALES_INTERNAL,
      RoleEnum.MASTER,
      RoleEnum.BACKOFFICE,
      RoleEnum.AREA_MANAGER,
    ].includes(userRole);
  }

  useEffect(() => {
    if (isBam) return;
    handleApiResponse(apiClient.salesforceAccountController.getSalesUsers())
      .then((res) => {
        setSalesUsers(res);
      })
      .catch((err) => {
        console.error('Error fetching sales users:', err);
      });
  }, [isBam]);

  useEffect(() => {
    handleApiResponse(apiClient.salesforceAccountController.getReferenceUsers())
      .then((res) => {
        const items: RemoteSelectOption[] = (res || []).map((item) =>
          _.isString(item)
            ? {
              value: item,
              label: item,
            }
            : item
        );
        setReferenceUsers(items);
      })
      .catch((err) => {
        console.error('Error fetching sales users:', err);
      });
  }, []);

  useEffect(() => {
    handleApiResponse(apiClient.salesforceAccountController.getBackupUsers())
      .then((res) => {
        setBackupUsers(res);
      })
      .catch((err) => {
        console.error('Error fetching sales users:', err);
      });
  }, []);

  const defaultReferenceUser = useMemo(() => {
    const firstValidOption = (referenceUsers || []).find(
      (option) => !option.disabled
    );

    if ([RoleEnum.BACKOFFICE, RoleEnum.AREA_MANAGER].includes(userRole)) {
      return firstValidOption?.value;
    }

    if (
      ![
        RoleEnum.SALES,
        RoleEnum.SALES_DEALER,
        RoleEnum.SALES_INTERNAL,
      ].includes(userRole)
    )
      return;

    const currentUserOption = (referenceUsers || []).find(
      (option) => option.value === user.username && !option.disabled
    );

    return userIsSalesforceSales && currentUserOption
      ? currentUserOption?.value
      : firstValidOption?.value;
  }, [referenceUsers, userRole, user, userIsSalesforceSales]);

  const marketplaceChannel = opportunityFields?.find(field => field?.name === 'Canale__c')?.picklistValues?.find(val => val?.value?.toLowerCase() === 'marketplace');
  const hmpBridgeLeadSource = opportunityFields?.find(field => field?.name === 'LeadSource')?.picklistValues?.find(val => val?.value?.toLowerCase() === 'hmp bridge') || {
    "label": "HMP Bridge",
    "value": "HMP Bridge"
  };
  const defaultChannel = userRole === RoleEnum.SALES_DEALER ? marketplaceChannel : undefined;

  return (
    <Formik
      enableReinitialize
      initialValues={{
        ...opportunityValues,
        name: opportunityValues.name.length
          ? opportunityValues.name
          : account?.name,
        nomePartner:
          userReferencePartnerList.length > 1
            ? null
            : {
              active: true,
              label: userReferencePartnerList[0],
              value: userReferencePartnerList[0],
            },
        salesUser:
          opportunityValues?.salesUser ||
          ([
            RoleEnum.SALES,
            RoleEnum.SALES_DEALER,
            RoleEnum.SALES_INTERNAL,
          ].includes(userRole)
            ? salesUsers?.[0]
            : undefined),
        referenceUserId:
          opportunityValues?.referenceUserId || defaultReferenceUser,
        Canale__c: defaultChannel,
      }}
      validationSchema={getOpportunityCreationValidationSchema(userRole)}
      onSubmit={submitFn}
    >
      {({
        errors,
        values,
        handleChange,
        handleSubmit,
        setFieldValue,
        setFieldTouched,
        setValues,
        dirty,
        touched,
      }) => {
        const selectedDealer = values.nomePartner?.value;
        const isSegnalazione = values.Canale__c?.value?.toLowerCase() === 'segnalatore';
        const filteredSalesUsers: UserDTO[] = selectedDealer
          ? allSalesUsersDTO.filter((u: UserDTO) => u.reference_partner?.includes(selectedDealer))
          : allSalesUsersDTO;
        const filteredSegnalatori: UserDTO[] = selectedDealer
          ? allSegnalatori.filter((u: UserDTO) => u.reference_partner?.includes(selectedDealer))
          : allSegnalatori;
        const selectedSegnalatorePerc = isSegnalazione
          ? allSegnalatori.find((u: UserDTO) => u.username === values.segnalatore)?.provvigioni_segnalatore
          : undefined;
        return (
          <Form onSubmit={handleSubmit}>
            <ForceCommissionsZeroOnSegnalazione
              isSegnalazione={isSegnalazione}
              isMasterOrBackoffice={isMasterOrBackoffice}
              setFieldValue={setFieldValue}
            />
            <ForceLeadSourceOnSegnalazione
              isSegnalazione={isSegnalazione}
              hmpBridgeLeadSource={hmpBridgeLeadSource}
              setFieldValue={setFieldValue}
            />
            <Row className='mt-4 mb-3 align-items-end'>
              <Col xs={3}>
                <Form.Group>
                  <RequiredFormLabel label={i18next.t('name')} />
                  <Form.Control type="text"
                    disabled
                    value={'HMP-' + (account ? `${account?.Name}-` : '')}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Control type="text"
                    name='name'
                    required
                    placeholder={i18next.t('input_name')}
                    value={values.name}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {commissionsLabel}
                    <span className="red-ast">*</span>
                    <small id="info-commissions-euro" className='text-muted ms-2'>
                      <FaInfoCircle size={18} />
                      <ToolTip
                        anchorId="info-commissions-euro"
                        content={t('info_commissions_euro')}
                        className="default"
                      />
                    </small>
                  </Form.Label>
                  <Form.Control type="text"
                    name='commissions'
                    disabled={isQuoteOpportunity || (isMasterOrBackoffice && isSegnalazione)}
                    value={values.commissions}
                    onChange={({ target }) => {
                      const next = renderFloatAsStr(target?.value);
                      setFieldValue(target.name, next);
                      if (isSegnalazione) {
                        applySegnalatoreCrossUpdate({
                          field: 'commissions',
                          value: next,
                          isMasterOrBackoffice,
                          segnalatorePerc: selectedSegnalatorePerc,
                          setFieldValue,
                        });
                      }
                    }}
                  />
                  <ValidationError {...{ errors, touched, name: "commissions" }} />
                </Form.Group>
              </Col>
              {isMasterOrBackoffice && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {t('provvigioni_dealer_euro')}
                      <span className="red-ast">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="provvigioniPassive"
                      value={values.provvigioniPassive || ''}
                      onChange={({ target }) => {
                        const next = renderFloatAsStr(target.value);
                        setFieldValue(target.name, next);
                        if (isSegnalazione) {
                          applySegnalatoreCrossUpdate({
                            field: 'provvigioniPassive',
                            value: next,
                            isMasterOrBackoffice,
                            segnalatorePerc: selectedSegnalatorePerc,
                            setFieldValue,
                          });
                        }
                      }}
                    />
                    <ValidationError {...{ errors, touched, name: "provvigioniPassive" }} />
                  </Form.Group>
                </Col>
              )}
              {opportunityFields?.map(el => {
                return (
                  <Col md={6} key={el.name}>
                    <Form.Group className="mb-3">
                      <RequiredFormLabel label={el.label} required={['nomePartner', 'Offerta_Edison__c'].includes(el?.name) ? false : undefined} />
                      <Select
                        menuPosition="fixed"
                        name={el.name}
                        isClearable={['Offerta_Edison__c'].includes(el.name)}
                        placeholder={`${t('select')} ${el.label.toLowerCase()}`}
                        options={el.picklistValues}
                        value={values[el.name]}
                        isDisabled={(['Noleggiatore__c'].includes(el.name) && isQuoteOpportunity) || (el.name === 'LeadSource' && isSegnalazione)}
                        onChange={(opt: any) => {
                          if (el?.name === 'nomePartner') {
                            setFieldValue('segnalatore', '');
                            setFieldTouched('segnalatore', true, true);
                            setFieldValue('salesUser', '');
                            setFieldTouched('salesUser', true, true);
                          }
                          if (
                            el?.name === 'Canale__c'
                            && isSegnalazione
                            && opt?.value?.toLowerCase() !== 'segnalatore'
                          ) {
                            setFieldValue('commissions', '');
                            setFieldValue('provvigioniPassive', '');
                            setFieldValue('provvigioniSegnalatore', '');
                            setFieldValue('segnalatore', '');
                            setFieldValue('LeadSource', null);
                          }
                          setFieldValue(el.name, opt)
                        }}
                      />
                    </Form.Group>
                  </Col>
                )
              })}
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <RequiredFormLabel label={t('sales_user')} name='salesUser' />
                  <Form.Select
                    name="salesUser"
                    value={values.salesUser || ''}
                    onChange={handleChange}
                  >
                    <option value="">{t('select_sales_user')}</option>
                    {isBam
                      ? filteredSalesUsers.map((u: UserDTO) => (
                        <option key={u.username} value={u.username}>{u.username}</option>
                      ))
                      : salesUsers && salesUsers.map((user: any) => (
                        <option key={user} value={user}>{user}</option>
                      ))
                    }
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <RequiredFormLabel label={t('reference_user')} name='referenceUserId'></RequiredFormLabel>
                  <Form.Select
                    name="referenceUserId"
                    required
                    value={values.referenceUserId || ''}
                    onChange={handleChange}
                  >
                    <option value="">{t('select_reference_user')}</option>
                    {referenceUsers && referenceUsers.map((user) => (
                      <option key={user.value} value={user.value} disabled={user.disabled}>
                        {user.label} {user.disabled ? `(${user.disabledReason})` : null}
                      </option>
                    ))}
                  </Form.Select>
                  <ValidationError {...{ errors, touched, name: "referenceUserId" }} />
                </Form.Group>
              </Col>
            </Row>
            {isBam && isSegnalazione && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <RequiredFormLabel label={t('provvigioni_segnalatore')} name="provvigioniSegnalatore" />
                    <Form.Control
                      type="text"
                      name="provvigioniSegnalatore"
                      value={values.provvigioniSegnalatore || ''}
                      onChange={({ target }) => {
                        const next = renderFloatAsStr(target.value);
                        setFieldValue(target.name, next);
                        applySegnalatoreCrossUpdate({
                          field: 'provvigioniSegnalatore',
                          value: next,
                          isMasterOrBackoffice,
                          segnalatorePerc: selectedSegnalatorePerc,
                          setFieldValue,
                        });
                      }}
                    />
                    <ValidationError {...{ errors, touched, name: "provvigioniSegnalatore" }} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <RequiredFormLabel label={'Segnalatore'} name="segnalatore" />
                    <Form.Select
                      name="segnalatore"
                      value={values.segnalatore || ''}
                      onChange={(e) => {
                        const selected = allSegnalatori.find((u: UserDTO) => u.username === e.target.value);
                        setValues({
                          ...values,
                          segnalatore: e.target.value,
                          ragioneSocialeSegnalatore: selected?.business_name || '',
                          commissions: isMasterOrBackoffice ? values.commissions : '',
                          provvigioniPassive: isMasterOrBackoffice ? '' : values.provvigioniPassive,
                          provvigioniSegnalatore: '',
                        }, true);
                      }}
                    >
                      <option value="">{t('select') + ' Segnalatore'}</option>
                      {filteredSegnalatori.map((u: UserDTO) => (
                        <option key={u.username} value={u.username}>
                          {u.username}
                        </option>
                      ))}
                    </Form.Select>
                    <ValidationError {...{ errors, touched, name: "segnalatore" }} />
                  </Form.Group>
                </Col>
              </Row>
            )}
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {t('backup_users')}
                    <small id="info-backup-users" className='text-muted ms-2'>
                      <FaInfoCircle size={18} />
                      <ToolTip
                        anchorId="info-backup-users"
                        content={t('opportunity_backup_users_mail_info')}
                        className="default"
                      />
                    </small>
                  </Form.Label>
                  <Select
                    menuPosition="fixed"
                    name="backupUsers"
                    noOptionsMessage={() => t('no_options')}
                    value={values.backupUsers ? values.backupUsers.map((user: string) => ({ label: user, value: user })) : []}
                    onChange={
                      (opts: any) => setFieldValue('backupUsers', opts ? opts.map((opt: any) => opt.value) : [])
                    }
                    options={backupUsers && backupUsers.map((user) => ({ label: user, value: user }))}
                    placeholder={t('select_backup_users')}
                    isMulti
                  />
                </Form.Group>
              </Col>
            </Row>
            <TiassistoElement>
              <Row className='align-items-end'>
                {/* DRIVE IT EASY SWITCH */}
                <Col md={6}>
                  <Form.Group className="mb-3 d-flex">
                    <Form.Label
                      className="me-3"
                      htmlFor="driveItEasyAnchor"
                    >
                      {t("drive_it_easy")}
                    </Form.Label>
                    <Form.Check
                      id="driveItEasyAnchor"
                      name="driveItEasy"
                      type="switch"
                      onChange={handleChange}
                      checked={values.driveItEasy}
                      className="hor-form-check-switch"
                      disabled={isButtonDisabled(userRole)}
                    />
                  </Form.Group>
                </Col>

                {/* INPUT DRIVER EMAIL */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <RequiredFormLabel label={i18next.t('driver_mail')} required={values.driveItEasy} errors={errors} name='driverEmail' />
                    <Form.Control
                      type='email'
                      id="driverEmailAnchor"
                      name="driverEmail"
                      value={values.driverEmail}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row >
            </TiassistoElement>

            <Row>
              <Col md={12}>
                <FormControl
                  as="textarea"
                  placeholder={`${i18next.t('add_notes')}...`}
                  name='notes'
                  onChange={handleChange}
                  style={{ height: '200px' }}
                  value={values.notes}
                />
              </Col>
            </Row>

            {/* {modalState.mode !== LeadsModalModeEnum.QUALIFIED && ( */}
            <Row className='mt-3'>
              <Col md={12}>
                <Btn
                  disabled={Object.keys(errors).length > 0}
                  type="submit"
                  icon="arrow-right"
                  className="float-end"
                >
                  {i18next.t('create')}
                </Btn>
              </Col>
            </Row>
            {/* )} */}

          </Form>
        )
      }}
    </Formik>
  );
};

export default OpportunityCreation;
